import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadGoogleMapsScript } from './maps_loader';
import { HIGH_VISIBILITY_MAP_STYLE } from './map_styles';
import { formatLocationAddress } from './client_locations_map';
import { formatRequestDate, getHomepageRequestCategory, HOMEPAGE_REQUEST_STATUS, requestValue } from './fire_request_dashboard';

const USA_CENTER = { lat: 39.8283, lng: -98.5795 };
const MARKER_PATH = 'M 0 0 C -2 -4 -10 -12 -10 -20 C -10 -26 -5 -31 0 -31 C 5 -31 10 -26 10 -20 C 10 -12 2 -4 0 0 Z';

export const requestMarkerIcon = category => ({
  path: MARKER_PATH,
  fillColor: HOMEPAGE_REQUEST_STATUS[category]?.color || HOMEPAGE_REQUEST_STATUS.scheduling.color,
  fillOpacity: 1,
  strokeColor: '#ffffff',
  strokeOpacity: 1,
  strokeWeight: 2,
  scale: 1.1
});

export const mapRequestRecord = request => {
  const latitudeValue = requestValue(request, 134);
  const longitudeValue = requestValue(request, 135);
  const latitude = Number(latitudeValue);
  const longitude = Number(longitudeValue);
  const category = getHomepageRequestCategory(request);
  return {
    id: String(requestValue(request, 3) || ''),
    code: String(requestValue(request, 10) || '').trim(),
    trade: String(requestValue(request, 7) || '').trim(),
    address: formatLocationAddress(requestValue(request, 15)),
    followUpDate: formatRequestDate(requestValue(request, 32)),
    category,
    status: HOMEPAGE_REQUEST_STATUS[category].label,
    position: latitudeValue !== '' && longitudeValue !== '' && Number.isFinite(latitude) && Number.isFinite(longitude) && Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180
      ? { lat: latitude, lng: longitude }
      : null
  };
};

const buildInfoContent = request => {
  const wrapper = document.createElement('div');
  wrapper.className = 'mapInfoWindow';
  const title = document.createElement('strong');
  title.textContent = `${request.code || 'Service request'}${request.trade ? ` – ${request.trade}` : ''}`;
  const status = document.createElement('div');
  status.textContent = request.status;
  const date = document.createElement('div');
  date.textContent = `Follow-up: ${request.followUpDate}`;
  const address = document.createElement('div');
  address.textContent = request.address;
  wrapper.append(title, status, date, address);
  return wrapper;
};

export const ServiceRequestsMap = ({ requests }) => {
  const mapNode = useRef(null);
  const navigate = useNavigate();
  const [mapState, setMapState] = useState({ status: 'loading', error: '' });
  const mappedRequests = useMemo(() => requests.map(mapRequestRecord).filter(request => request.position), [requests]);

  useEffect(() => {
    if (!requests.length || !mappedRequests.length) {
      setMapState({ status: 'empty', error: '' });
      return undefined;
    }
    let cancelled = false;
    let markers = [];
    const initialize = async () => {
      setMapState({ status: 'loading', error: '' });
      try {
        await loadGoogleMapsScript();
        if (cancelled || !mapNode.current) return;
        const map = new window.google.maps.Map(mapNode.current, { center: USA_CENTER, zoom: 4, styles: HIGH_VISIBILITY_MAP_STYLE });
        const infoWindow = new window.google.maps.InfoWindow();
        const bounds = window.google.maps.LatLngBounds ? new window.google.maps.LatLngBounds() : null;
        mappedRequests.forEach(request => {
          const marker = new window.google.maps.Marker({
            map, position: request.position, title: `${request.code} – ${request.status}`, icon: requestMarkerIcon(request.category)
          });
          marker.addListener('mouseover', () => {
            infoWindow.setContent(buildInfoContent(request));
            infoWindow.open({ map, anchor: marker });
          });
          marker.addListener('mouseout', () => infoWindow.close());
          marker.addListener('click', () => navigate(`/serviceTicket/${request.id}?app=fire_pest`, { state: { id: request.id } }));
          if (bounds) bounds.extend(request.position);
          markers.push(marker);
        });
        if (bounds && mappedRequests.length > 1) map.fitBounds(bounds, 52);
        else if (mappedRequests.length === 1) {
          map.setCenter(mappedRequests[0].position);
          map.setZoom(10);
        }
        if (!cancelled) setMapState({ status: 'ready', error: '' });
      } catch (error) {
        if (!cancelled) setMapState({ status: 'error', error: error.message || 'Unable to load the map.' });
      }
    };
    initialize();
    return () => {
      cancelled = true;
      markers.forEach(marker => marker.setMap(null));
      markers = [];
    };
  }, [mappedRequests, navigate, requests.length]);

  return (
    <section className="mapCard fireRequestsMapCard" aria-labelledby="requests-map-title">
      <div className="mapCardHeader"><div><h2 id="requests-map-title">Service request map</h2><p>Requests with follow-up dates within 30 days of today</p></div></div>
      {mapState.status === 'empty' && <div className="mapState">No requests with map coordinates are available in this date range.</div>}
      {mapState.status === 'error' && <div className="mapState mapError" role="alert">Map unavailable — {mapState.error}</div>}
      <div className={`mapCanvasWrapper ${mapState.status === 'empty' || mapState.status === 'error' ? 'mapHidden' : ''}`}>
        {mapState.status === 'loading' && <div className="mapState mapStateOverlay" role="status">Loading request map…</div>}
        <div ref={mapNode} className="locationsMap" aria-label="Map of service requests" />
      </div>
    </section>
  );
};
